import type { EmailBodyDto } from '@carbonwebmail/contracts'
import { emailBodySchema } from '@carbonwebmail/contracts'

/**
 * Handing a message to the preview tab.
 *
 * The preview is a tab of its own rather than a panel over the canvas, because
 * what somebody is checking is how the message will *arrive* — at the width of
 * a phone, with its links followed — and a box in the middle of a builder
 * answers a smaller question than that.
 *
 * **What is being previewed has not been saved.** That is the point of a
 * preview, so the tab cannot fetch it by id; it has to be handed over. It goes
 * through `sessionStorage`, which a new tab opened from this one starts with a
 * copy of, and which dies with the tab. Not `localStorage`: that would put
 * somebody's draft on disk for every later session of this browser to find, and
 * storing application state there is the habit this rebuild exists to leave
 * behind.
 *
 * Both halves live here so the key is written and read in one place. A tab that
 * finds nothing says so rather than showing an older message — a preview
 * nobody asked for is worse than none, because it looks like an answer.
 */

export interface PreviewHandoff {
  readonly subject: string
  readonly body: EmailBodyDto
}

const KEY = 'carbonwebmail.preview'

const PATH = '/preview'

/**
 * Opens the tab, having put the message where it will look for it.
 *
 * Both in one function, and called straight from the click: a browser only
 * allows `window.open` while it is still handling the press, so anything
 * awaited in between turns the preview into a blocked popup.
 *
 * No `noopener`: it is what tells the browser this is a fresh, unrelated
 * context, and a fresh context does not inherit the session storage the message
 * was just written into.
 */
export function openPreviewTab(handoff: PreviewHandoff): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(handoff))
  } catch {
    // Private windows and locked-down profiles refuse storage outright. The
    // tab still opens and says it has nothing to show, which is honest and is
    // what the reader can act on.
  }
  window.open(PATH, '_blank')
}

/** What the tab was opened to show, or nothing if it was opened by hand. */
export function readPreviewHandoff(): PreviewHandoff | undefined {
  try {
    const written = window.sessionStorage.getItem(KEY)
    if (written === null) return undefined

    const parsed: unknown = JSON.parse(written)
    if (typeof parsed !== 'object' || parsed === null) return undefined

    const { subject, body } = parsed as { subject?: unknown; body?: unknown }
    const checked = emailBodySchema.safeParse(body)
    // Validated rather than trusted, even though this application wrote it: the
    // shape may have been written by an older bundle in a tab that has been
    // open all afternoon.
    if (typeof subject !== 'string' || !checked.success) return undefined

    return { subject, body: checked.data }
  } catch {
    return undefined
  }
}
